/* ============================================================
   NOBARUEN THEME — global.js
   ============================================================ */

(function () {
  'use strict';

  const theme = window.theme || {};

  /* ---------- Utilities ---------- */
  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* ---------- Sticky header ---------- */
  const header = document.querySelector('[data-header]');
  if (header && theme.settingsSticky) {
    const onScroll = debounce(() => {
      header.classList.toggle('is-sticky', window.scrollY > 10);
    }, 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Drawer (mobile menu) ---------- */
  const drawer = document.querySelector('[data-drawer]');
  const overlay = document.getElementById('overlay');

  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    if (overlay) {
      overlay.hidden = false;
      overlay.classList.add('is-visible');
    }
    document.body.style.overflow = 'hidden';
    const toggle = document.querySelector('[data-drawer-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) {
      overlay.classList.remove('is-visible');
    }
    document.body.style.overflow = '';
    setTimeout(() => { drawer.hidden = true; }, 320);
    const toggle = document.querySelector('[data-drawer-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  document.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
    btn.addEventListener('click', openDrawer);
  });
  document.querySelectorAll('[data-drawer-close]').forEach((btn) => {
    btn.addEventListener('click', closeDrawer);
  });

  /* Mobile submenu toggles */
  document.querySelectorAll('.mobile-nav__toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const sub = btn.parentElement.querySelector('.mobile-nav__sub');
      if (sub) sub.hidden = expanded;
    });
  });

  /* ---------- Overlay click closes drawers ---------- */
  if (overlay) {
    overlay.addEventListener('click', () => {
      closeDrawer();
      closeCartDrawer();
    });
  }

  /* ---------- Search toggle ---------- */
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-search-panel]');
  if (searchToggle && searchPanel) {
    searchToggle.addEventListener('click', () => {
      const isHidden = searchPanel.hidden;
      searchPanel.hidden = !isHidden;
      if (isHidden) {
        const input = searchPanel.querySelector('[data-predictive-search-input]');
        if (input) input.focus();
      }
    });
  }

  /* ---------- Hero slider ---------- */
  document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
    const track = slider.querySelector('[data-hero-track]');
    const slides = slider.querySelectorAll('[data-hero-slide]');
    const dotsWrap = slider.querySelector('[data-hero-dots]');
    const prevBtn = slider.querySelector('[data-hero-prev]');
    const nextBtn = slider.querySelector('[data-hero-next]');
    if (!track || slides.length < 2) return;

    let index = 0;
    let timer = null;
    const autoplay = slider.dataset.autoplay !== 'false';
    const interval = (parseInt(slider.dataset.autoplaySpeed, 10) || 5) * 1000;

    function goTo(i) {
      index = (i + slides.length) % slides.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      if (dotsWrap) {
        dotsWrap.querySelectorAll('.hero-slider__dot').forEach((dot, di) => {
          dot.classList.toggle('is-active', di === index);
        });
      }
    }

    function start() {
      if (!autoplay) return;
      stop();
      timer = setInterval(() => goTo(index + 1), interval);
    }
    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    if (dotsWrap) {
      slides.forEach((_, si) => {
        const dot = document.createElement('button');
        dot.className = 'hero-slider__dot' + (si === 0 ? ' is-active' : '');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Slide ${si + 1}`);
        dot.addEventListener('click', () => { goTo(si); start(); });
        dotsWrap.appendChild(dot);
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => { goTo(index - 1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { goTo(index + 1); start(); });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    start();
  });

  /* ---------- Product gallery ---------- */
  const gallery = document.querySelector('[data-product-gallery]');
  if (gallery) {
    const slides = gallery.querySelectorAll('.product-gallery__slide');
    const thumbs = gallery.querySelectorAll('[data-thumb]');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const id = thumb.dataset.thumb;
        slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === id));
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      });
    });
  }

  /* ---------- Quantity buttons (product page & cart) ---------- */
  function bindQty(container) {
    const minus = container.querySelector('[data-qty-minus]');
    const plus = container.querySelector('[data-qty-plus]');
    const input = container.querySelector('[data-qty-input]');
    if (!minus || !plus || !input) return;

    function update(value) {
      value = Math.max(1, parseInt(value, 10) || 1);
      input.value = value;
      minus.disabled = value <= 1;
    }

    minus.addEventListener('click', () => update(parseInt(input.value, 10) - 1));
    plus.addEventListener('click', () => update(parseInt(input.value, 10) + 1));
    input.addEventListener('change', () => update(parseInt(input.value, 10) || 1));
  }

  document.querySelectorAll('.product-form__qty').forEach(bindQty);

  /* Cart page qty: live update via /cart/change.js */
  const cartQtyInputs = document.querySelectorAll('[data-cart-qty-input]');
  if (cartQtyInputs.length) {
    function refreshCartPage() {
      window.location.reload();
    }
    const changeCartQty = debounce((line, qty) => {
      if (qty < 1) qty = 1;
      fetch(`${theme.routes.cartChange}?line=${line + 1}&quantity=${qty}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' }
      })
        .then((r) => r.json())
        .then(() => refreshCartPage())
        .catch(() => refreshCartPage());
    }, 500);

    cartQtyInputs.forEach((input) => {
      const line = parseInt(input.dataset.line, 10);
      input.addEventListener('change', () => changeCartQty(line, parseInt(input.value, 10) || 1));
    });

    document.querySelectorAll('[data-cart-qty-minus]').forEach((btn) => {
      const line = parseInt(btn.dataset.line, 10);
      const input = btn.parentElement.querySelector('[data-cart-qty-input]');
      btn.addEventListener('click', () => {
        const v = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
        input.value = v;
        changeCartQty(line, v);
      });
    });

    document.querySelectorAll('[data-cart-qty-plus]').forEach((btn) => {
      const line = parseInt(btn.dataset.line, 10);
      const input = btn.parentElement.querySelector('[data-cart-qty-input]');
      btn.addEventListener('click', () => {
        const v = (parseInt(input.value, 10) || 1) + 1;
        input.value = v;
        changeCartQty(line, v);
      });
    });
  }

  /* ---------- Cart drawer ---------- */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartToggle = document.querySelector('[data-cart-toggle]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.hidden = false;
    requestAnimationFrame(() => cartDrawer.classList.add('is-open'));
    if (overlay) {
      overlay.hidden = false;
      overlay.classList.add('is-visible');
    }
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    if (overlay) {
      overlay.classList.remove('is-visible');
    }
    document.body.style.overflow = '';
    setTimeout(() => { cartDrawer.hidden = true; }, 320);
  }

  /* ---------- Add to cart (AJAX) ---------- */
  function updateCartBadge(count) {
    const badge = document.querySelector('[data-cart-count]');
    if (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
  }

  async function addToCart(form) {
    const body = new FormData(form);
    const res = await fetch(theme.routes.cartAdd, { method: 'POST', body });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'error');
    return data;
  }

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const res = await fetch(`${theme.routes.cartUrl}?section_id=cart-drawer`);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('[data-cart-drawer]');
      if (newDrawer) {
        cartDrawer.innerHTML = newDrawer.innerHTML;
        bindCartDrawerClose();
      }
    } catch (e) {
      /* fall back to page reload */
      window.location.href = theme.routes.cartUrl;
    }
  }

  function bindCartDrawerClose() {
    document.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      btn.removeEventListener('click', closeCartDrawer);
      btn.addEventListener('click', closeCartDrawer);
    });
  }

  if (cartToggle) {
    cartToggle.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  }
  bindCartDrawerClose();

  /* Product form (main product page) */
  document.querySelectorAll('form[data-add-to-cart-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-add-to-cart]');
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '...';
      try {
        const data = await addToCart(form);
        updateCartBadge(data.item_count);
        if (theme.cartDrawer) {
          await refreshCartDrawer();
          openCartDrawer();
        } else {
          window.location.href = theme.routes.cartUrl;
        }
      } catch (err) {
        btn.textContent = theme.strings.cartError;
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      }
    });
  });

  /* Quick add on product cards */
  document.querySelectorAll('form[data-quick-add]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button');
      const originalText = btn.textContent;
      btn.disabled = true;
      try {
        const data = await addToCart(form);
        updateCartBadge(data.item_count);
        if (theme.cartDrawer) {
          await refreshCartDrawer();
          openCartDrawer();
        } else {
          window.location.href = theme.routes.cartUrl;
        }
      } catch (err) {
        btn.textContent = theme.strings.cartError;
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      } finally {
        btn.disabled = false;
      }
    });
  });

  /* ---------- Collection sort ---------- */
  const sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* ---------- Variant selection (product page) ---------- */
  const variantSelects = document.querySelector('[data-section][data-url]');
  if (variantSelects) {
    const radios = variantSelects.querySelectorAll('input[type="radio"][data-option-position]');
    const addBtn = document.querySelector('[data-add-to-cart]');
    const priceWrapper = document.querySelector('[data-price-wrapper]');
    const hiddenId = variantSelects.closest('form').querySelector('input[name="id"]');

    function currentSelection() {
      const selected = {};
      radios.forEach((r) => {
        if (r.checked) selected[r.dataset.optionPosition] = r.value;
      });
      return selected;
    }

    function findVariant() {
      const sel = currentSelection();
      const variants = JSON.parse(variantSelects.querySelector('[data-variant-json]').textContent);
      return variants.find((v) =>
        v.options.every((opt, i) => opt === sel[String(i + 1)])
      );
    }

    function updateForm() {
      const variant = findVariant();
      if (!variant) return;
      if (hiddenId) hiddenId.value = variant.id;
      if (addBtn) {
        if (!variant.available) {
          addBtn.disabled = true;
          addBtn.textContent = theme.strings.soldOut;
        } else {
          addBtn.disabled = false;
          addBtn.textContent = theme.strings.addToCart;
        }
      }
      if (priceWrapper) {
        const html = variant.compare_at_price && variant.compare_at_price > variant.price
          ? `<div class="price price--on-sale"><span class="price__compare">${formatMoney(variant.compare_at_price)}</span><span class="price__current">${formatMoney(variant.price)}</span></div>`
          : `<div class="price"><span class="price__current">${formatMoney(variant.price)}</span></div>`;
        priceWrapper.innerHTML = html;
      }
    }

    radios.forEach((r) => r.addEventListener('change', updateForm));
  }

  function formatMoney(cents) {
    try {
      const amount = (cents / 100).toFixed(2);
      const format = theme.strings.moneyFormat || '${{amount}}';
      return format.replace('{{amount}}', amount);
    } catch (e) {
      return String(cents);
    }
  }

  /* ---------- Address forms toggle ---------- */
  const newToggle = document.querySelector('[data-address-new-toggle]');
  const newForm = document.querySelector('[data-address-new]');
  if (newToggle && newForm) {
    newToggle.addEventListener('click', () => { newForm.hidden = !newForm.hidden; });
    const closeBtn = newForm.querySelector('[data-address-new-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => { newForm.hidden = true; });
  }

  document.querySelectorAll('[data-address-edit]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.addressEdit;
      const form = document.querySelector(`[data-address-edit-form="${id}"]`);
      if (form) form.hidden = !form.hidden;
    });
  });

  document.querySelectorAll('[data-address-edit-close]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.addressEditClose;
      const form = document.querySelector(`[data-address-edit-form="${id}"]`);
      if (form) form.hidden = true;
    });
  });

  /* Country/province dependent forms — keep simple: province shown always */
})();
